import { useMutation, useQueryClient } from "@tanstack/react-query";
import { pathPickSearchProductsMutationApi } from "@/features/pathpick/features/search-products/api/mutation";
import { PathPickBasketRequestBody } from "@/features/pathpick/features/search-products/types/request-body";
import { PathPickProductsResponse } from "@/features/pathpick/types/response";
import { refreshPathPickProducts } from "@/features/pathpick/utils/query-cache";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

const useAddPathPickBasket = () => {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  const { t } = useI18nHelper();
  const { info, error } = useAppToasts({ id: "GLOBAL" });

  const productsKey = ["pathpick", "api-products", companyId];
  const productIdsKey = ["pathpick", "api-product-ids", companyId];
  const countsKey = ["pathpick", "counts", companyId];
  const basketFilterInfoKey = ["pathpick", "basket", "filter-info", companyId];
  const basketCountsKey = ["pathpick", "basket", "counts", companyId];

  return useMutation({
    mutationFn: (body: PathPickBasketRequestBody) =>
      pathPickSearchProductsMutationApi.addPathPickBasket(
        companyId as number,
        body
      ),

    onMutate: async (body) => {
      const ids = new Set(body.products.map((product) => product.id)); // 상품 순회 시 O(1) 포함여부 조회
      // 진행 중인 products refetch가 낙관적 결과를 덮어쓰지 않도록 취소
      await queryClient.cancelQueries({ queryKey: productsKey });

      // 롤백용 스냅샷 (모든 파라미터/페이지 변형 포함)
      const prevProducts = queryClient.getQueriesData<PathPickProductsResponse>(
        {
          queryKey: productsKey,
        }
      );

      // 담은 상품을 isInBasket=true 로 즉시 반영
      queryClient.setQueriesData<PathPickProductsResponse>(
        { queryKey: productsKey },
        (old) =>
          old
            ? {
                ...old,
                products: old.products.map((product) =>
                  ids.has(product.id)
                    ? { ...product, isInBasket: true }
                    : product
                ),
              }
            : old
      );

      return { prevProducts };
    },

    onError: (err, _body, context) => {
      // 실패 시 스냅샷으로 롤백
      context?.prevProducts?.forEach(([key, data]) =>
        queryClient.setQueryData(key, data)
      );
      // 서버 에러 메시지를 우선 파싱하고, 없으면 기본 안내를 노출한다.
      error(err);
    },

    onSuccess: () => {
      info(t("path_pick:search_products.basket.add_success"));
    },

    // 성공/실패 무관하게 서버 상태와 최종 동기화한다.
    // (헤더 "내가 담은 상품" 카운트도 여기 invalidate로 갱신)
    onSettled: () => {
      // 화면에 없는 담은 상품 목록은 비워, 재진입 시 새로 담은 상품이 뒤늦게 끼어들지 않게 한다.
      refreshPathPickProducts(queryClient, companyId);
      // 담은 상품이 전체 선택 대상(isInBasket: false)에서 빠지도록 n도 다시 받는다.
      queryClient.invalidateQueries({ queryKey: productIdsKey });
      // 담은 상품 수 배지
      queryClient.invalidateQueries({ queryKey: countsKey });
      // 담은 상품 기준으로 뽑는 필터 옵션(브랜드/카테고리)
      queryClient.invalidateQueries({ queryKey: basketFilterInfoKey });
      // 담은 상품 탭 배지 카운트(작업 전/등록 신청함/주문함)
      queryClient.invalidateQueries({ queryKey: basketCountsKey });
    },
  });
};

export default useAddPathPickBasket;
